import { usePricingPageStore } from "@/state/store/pricingPageStore";
import { useBrandStore } from "@/state/store/brand";
import { useSearchSpecificationStore } from "@/state/store/searchSpecification";
import { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "./supabase";

// Errors that indicate an invalid session state requiring logout
const SESSION_INVALID_ERRORS = [
  "Organization not found",
  "User profile not found",
];

/**
 * Check if an error indicates the user's session is invalid and they should be logged out.
 * This handles cases where the user's organization was deleted, profile doesn't exist, etc.
 */
const isSessionInvalidError = (error: Error): boolean => {
  return SESSION_INVALID_ERRORS.some((msg) =>
    error.message.toLowerCase().includes(msg.toLowerCase())
  );
};

/**
 * Clear all persisted store data on logout.
 */
const clearAllStores = () => {
  useBrandStore.getState().clear();
  useSearchSpecificationStore.getState().clear();
};

/**
 * Handle session invalid errors by logging out and redirecting to auth.
 */
const handleSessionInvalidError = async (error: Error) => {
  toast.error("Your session is invalid. Please sign in again.");
  console.error("Session invalid error, logging out:", error.message);
  clearAllStores();
  await supabase.auth.signOut();
  window.location.href = "/auth";
};

/**
 * Handle known application errors with appropriate actions.
 */
const handleApplicationError = (error: Error) => {
  switch (error.message) {
    case "INSUFFICIENT_CREDITS":
      toast.error("You don't have enough credits to run this search");
      window.location.href = "/billing";
      break;
    case "UPGRADE_TO_PAID_PLAN":
      toast.error("You need to upgrade to a paid plan to run this search");
      usePricingPageStore.getState().setDisplayPricingModal(true);
      break;
    case "NO_ACTIVE_PROMPTS":
      toast.error("You don't have any active prompts to run this search");
      break;
    case "NO_ACTIVE_LLMS":
      toast.error("You don't have any active LLMs to run this search");
      break;
    case "SEARCH_ALREADY_RUNNING":
      toast.error("Search is already running");
      break;
    default:
      toast.error(error.message);
      break;
  }
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry if session is invalid - user needs to re-authenticate
        if (isSessionInvalidError(error as Error)) {
          handleSessionInvalidError(error as Error);
          return false;
        }
        // Default retry behavior for other errors
        return failureCount < 3;
      },
    },
    mutations: {
      onError: (error) => {
        // Check for session invalid errors first
        if (isSessionInvalidError(error)) {
          handleSessionInvalidError(error);
          return;
        }
        // Handle other known errors
        handleApplicationError(error);
      },
    },
  },
});
